import { useState } from 'react';

import { PublicTopBar } from 'components/PublicTopBar';
import { formatDateShort, getInitials } from 'utils/formatters';

import * as styled from './styles';

type Props = {
  quote: Kudable.QuotePublic | null;
  loadError: string | null;
  submitting: boolean;
  acceptedBookingToken: string | null;
  declined: boolean;
  onAccept: () => void;
  onDecline: (reason?: string) => void;
};

const FALLBACK_ACCENT = '#0F766E';

const formatAmount = (cents: number, currency: string) => {
  const rands = Math.floor(cents / 100);
  const cents_part = String(cents % 100).padStart(2, '0');
  return `${currency} ${rands}.${cents_part}`;
};

const formatRange = (start: string | null, end: string | null) => {
  if (!start) return 'TBD';
  if (!end || start === end) return formatDateShort(start);
  return `${formatDateShort(start)} – ${formatDateShort(end)}`;
};

const providerName = (provider: Kudable.QuotePublic['provider']) => {
  if (!provider) return 'Your provider';
  if (provider.business_name) return provider.business_name;
  return (
    [provider.first_name, provider.last_name].filter(Boolean).join(' ').trim() ||
    'Your provider'
  );
};

const providerInitials = (provider: Kudable.QuotePublic['provider']) => {
  if (!provider) return '·';
  if (provider.business_name) {
    return provider.business_name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join('');
  }
  return getInitials(provider.first_name ?? '', provider.last_name ?? '');
};

export const QuoteScene = ({
  quote,
  loadError,
  submitting,
  acceptedBookingToken,
  declined,
  onAccept,
  onDecline,
}: Props) => {
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  if (loadError) {
    return (
      <div>
        <PublicTopBar />
        <styled.Container>
          <styled.Card>
            <styled.Title>Quote not found</styled.Title>
            <styled.Body>{loadError}</styled.Body>
          </styled.Card>
        </styled.Container>
      </div>
    );
  }

  if (!quote) {
    return (
      <div>
        <PublicTopBar />
        <styled.Container>
          <styled.Card>
            <styled.Body>Loading…</styled.Body>
          </styled.Card>
        </styled.Container>
      </div>
    );
  }

  const accent = quote.provider?.accent_color || FALLBACK_ACCENT;
  const provider = providerName(quote.provider);
  const initials = providerInitials(quote.provider);
  const isSuperseded = quote.status === 'superseded';
  const isExpired = quote.status === 'expired';
  const isTerminal =
    quote.status === 'accepted' ||
    quote.status === 'declined' ||
    isSuperseded ||
    isExpired;

  if (acceptedBookingToken) {
    return (
      <div>
        <PublicTopBar />
        <styled.Container>
          <styled.Card>
            <styled.Title>Quote accepted</styled.Title>
            <styled.Body>
              Thanks! {provider} has been notified and will be in touch about
              next steps. A confirmation has been emailed to you.
            </styled.Body>
            <styled.PrimaryBtn
              $accent={accent}
              as="a"
              href={`/bookings/${acceptedBookingToken}`}
            >
              View confirmation
            </styled.PrimaryBtn>
          </styled.Card>
        </styled.Container>
      </div>
    );
  }

  if (declined) {
    return (
      <div>
        <PublicTopBar />
        <styled.Container>
          <styled.Card>
            <styled.Title>Quote declined</styled.Title>
            <styled.Body>We've let {provider} know.</styled.Body>
          </styled.Card>
        </styled.Container>
      </div>
    );
  }

  const dateSent = quote.sent_at ?? quote.proposed_start;
  const showClientBlock =
    !!quote.client_name || !!quote.client_email || !!quote.client_phone;

  return (
    <div>
      <PublicTopBar />
      <styled.Container>
        <styled.Sheet $accent={accent}>
          <styled.AccentStrip $accent={accent} />
          <styled.SheetBody>
            <styled.DocHeader>
              <styled.HeaderLeft>
                <styled.LogoBlock $accent={accent}>
                  {quote.provider?.logo_url ? (
                    <styled.LogoImage src={quote.provider.logo_url} alt="" />
                  ) : quote.provider?.avatar_url ? (
                    <styled.LogoImage
                      src={quote.provider.avatar_url}
                      alt=""
                      $cover
                    />
                  ) : (
                    <styled.LogoFallback $accent={accent}>
                      {initials}
                    </styled.LogoFallback>
                  )}
                </styled.LogoBlock>
                <styled.BusinessBlock>
                  <styled.BusinessName>{provider}</styled.BusinessName>
                  {quote.provider?.tagline ? (
                    <styled.BusinessTagline>
                      {quote.provider.tagline}
                    </styled.BusinessTagline>
                  ) : null}
                </styled.BusinessBlock>
              </styled.HeaderLeft>

              <styled.HeaderRight>
                <styled.QuoteEyebrow $accent={accent}>
                  QUOTE
                </styled.QuoteEyebrow>
                <styled.QuoteRef>{quote.quote_ref}</styled.QuoteRef>
                <styled.MetaTable>
                  {dateSent ? (
                    <>
                      <styled.MetaLabel>Date</styled.MetaLabel>
                      <styled.MetaValue>
                        {formatDateShort(dateSent)}
                      </styled.MetaValue>
                    </>
                  ) : null}
                  {quote.valid_until ? (
                    <>
                      <styled.MetaLabel>Valid until</styled.MetaLabel>
                      <styled.MetaValue>
                        <styled.ValidPill $accent={accent}>
                          {formatDateShort(quote.valid_until)}
                        </styled.ValidPill>
                      </styled.MetaValue>
                    </>
                  ) : null}
                </styled.MetaTable>
              </styled.HeaderRight>
            </styled.DocHeader>

            <styled.Parties>
              <styled.PartyBlock>
                <styled.PartyLabel>From</styled.PartyLabel>
                <styled.PartyName>{provider}</styled.PartyName>
                {quote.provider?.tagline ? (
                  <styled.PartyLine>{quote.provider.tagline}</styled.PartyLine>
                ) : null}
              </styled.PartyBlock>
              {showClientBlock ? (
                <styled.PartyBlock>
                  <styled.PartyLabel>Bill to</styled.PartyLabel>
                  {quote.client_name ? (
                    <styled.PartyName>{quote.client_name}</styled.PartyName>
                  ) : null}
                  {quote.client_email ? (
                    <styled.PartyLine>{quote.client_email}</styled.PartyLine>
                  ) : null}
                  {quote.client_phone ? (
                    <styled.PartyLine>{quote.client_phone}</styled.PartyLine>
                  ) : null}
                </styled.PartyBlock>
              ) : null}
            </styled.Parties>

            {quote.title ? (
              <styled.QuoteTitleSection>{quote.title}</styled.QuoteTitleSection>
            ) : null}

            {isSuperseded ? (
              <styled.Banner>
                This quote has been replaced by a newer one. Please check your
                email for the latest version.
              </styled.Banner>
            ) : null}
            {isExpired ? (
              <styled.Banner>
                This quote has expired. Please contact {provider} for an updated
                version.
              </styled.Banner>
            ) : null}

            <styled.LineItemsTable $accent={accent}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {quote.line_items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>
                      {formatAmount(item.line_total_cents, quote.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </styled.LineItemsTable>

            <styled.DetailRow>
              <styled.DetailLabel>Proposed dates</styled.DetailLabel>
              <styled.DetailValue>
                {formatRange(quote.proposed_start, quote.proposed_end)}
              </styled.DetailValue>
            </styled.DetailRow>

            <styled.TotalRow>
              <styled.TotalCard $accent={accent}>
                <styled.TotalLabel $accent={accent}>Total</styled.TotalLabel>
                <styled.TotalValue $accent={accent}>
                  {formatAmount(quote.total_cents, quote.currency)}
                </styled.TotalValue>
              </styled.TotalCard>
            </styled.TotalRow>

            {quote.note ? (
              <styled.NotesSection>
                <styled.NotesLabel>Notes</styled.NotesLabel>
                <styled.NotesBody>{quote.note}</styled.NotesBody>
              </styled.NotesSection>
            ) : null}

            {!isTerminal ? (
              <styled.Actions>
                <styled.PrimaryBtn
                  $accent={accent}
                  onClick={onAccept}
                  disabled={submitting}
                >
                  {submitting ? 'Working…' : 'Accept quote'}
                </styled.PrimaryBtn>
                <styled.SecondaryBtn
                  onClick={() => setShowDeclineForm((isShowing) => !isShowing)}
                >
                  Decline
                </styled.SecondaryBtn>
              </styled.Actions>
            ) : null}

            {showDeclineForm && !isTerminal ? (
              <styled.DeclineForm>
                <styled.DeclineLabel>
                  Optional — let {provider} know why
                </styled.DeclineLabel>
                <styled.DeclineInput
                  value={declineReason}
                  onChange={(event) => setDeclineReason(event.target.value)}
                  placeholder="e.g. Going with a different option, thanks anyway."
                  rows={3}
                />
                <styled.SecondaryBtn
                  onClick={() => onDecline(declineReason.trim() || undefined)}
                  disabled={submitting}
                >
                  {submitting ? 'Sending…' : 'Send decline'}
                </styled.SecondaryBtn>
              </styled.DeclineForm>
            ) : null}

            <styled.Footer>
              Questions about this quote? Reach out to {provider}.
            </styled.Footer>
          </styled.SheetBody>
        </styled.Sheet>
      </styled.Container>
    </div>
  );
};
